import React from "react";
import { Link } from "react-router-dom";
import { Anchor, AlertTriangle, Cloud, CloudRain, Droplets, Eye, Gauge, MapPin, ShieldCheck, Sun, Sunrise, Sunset, Waves, Wind } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { inferCloudName } from "@/components/weather/cloudTypesData";
import WeatherMetricTopicGroup from "@/components/weather/WeatherMetricTopicGroup";
import { getPublicWeatherTranslations } from "@/i18n";

export default function WeatherCommandDashboard({ windKnots, gustKnots, waveM, safety, beaufort, forecast, marine, locationName, lang = "en" }) {
  const copy = getPublicWeatherTranslations(lang).command;
  const score = Math.max(0, Math.min(100, Math.round(100 - windKnots * 1.35 - waveM * 16 - Math.max(0, gustKnots - windKnots) * 1.6)));
  const formatSunTime = (value) => value
    ? new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "—";
  const todayForecast = {
    windMax: Math.round(forecast?.daily?.wind_speed_10m_max?.[0] ?? windKnots),
    gustMax: Math.round(forecast?.daily?.wind_gusts_10m_max?.[0] ?? gustKnots),
    waveMax: +(marine?.daily?.wave_height_max?.[0] ?? waveM).toFixed(1),
    swellMax: +(marine?.daily?.swell_wave_height_max?.[0] ?? 0).toFixed(1),
    tempMax: Math.round(forecast?.daily?.temperature_2m_max?.[0] ?? 0),
    sunrise: formatSunTime(forecast?.daily?.sunrise?.[0]),
    sunset: formatSunTime(forecast?.daily?.sunset?.[0]),
    cloudCover: Math.round(forecast?.current?.cloud_cover ?? 0),
    rain: +(forecast?.current?.precipitation ?? 0).toFixed(1),
    pressureHpa: +(forecast?.current?.pressure_msl ?? 0).toFixed(1),
    visibilityKm: +((forecast?.current?.visibility ?? 0) / 1000).toFixed(1),
    uvIndex: +(forecast?.current?.uv_index ?? forecast?.hourly?.uv_index?.[0] ?? forecast?.daily?.uv_index_max?.[0] ?? 0).toFixed(1),
    humidity: Math.round(forecast?.current?.relative_humidity_2m ?? forecast?.hourly?.relative_humidity_2m?.[0] ?? 0),
  };
  const cloudType = todayForecast.rain > 0
    ? copy.states.rainClouds
    : todayForecast.cloudCover < 25
      ? copy.states.clearSky
      : todayForecast.cloudCover < 60
        ? copy.states.scatteredClouds
        : copy.states.overcast;
  const cloudName = inferCloudName({
    cloudCover: todayForecast.cloudCover,
    rain: todayForecast.rain,
    windKnots,
    gustKnots,
  });
  const pressureNow = forecast?.hourly?.pressure_msl?.[0] ?? todayForecast.pressureHpa;
  const pressureSoon = forecast?.hourly?.pressure_msl?.[3] ?? pressureNow;
  const pressureChange = pressureSoon - pressureNow;
  const stormSignals = [
    pressureChange <= -2,
    Math.max(0, gustKnots - windKnots) >= 10,
    todayForecast.cloudCover >= 80,
    todayForecast.rain > 0,
  ].filter(Boolean).length;
  const stormTrend = stormSignals >= 3 || pressureChange <= -3
    ? { value: copy.states.rising, helper: copy.states.stormSignals }
    : stormSignals >= 2 || pressureChange <= -1.5
      ? { value: copy.states.watch, helper: copy.states.changing }
      : { value: copy.states.stable, helper: copy.states.noSignal };
  const displayCity = locationName?.city && locationName.city !== "Detecting city..." ? locationName.city : copy.yourLocation;
  const displayLocation = [displayCity, locationName?.country].filter(Boolean).join(", ");
  const guidePrefix = `/${lang}/weather`;
  const safetyLabel = ({ danger: copy.states.dangerous, warning: copy.states.caution, moderate: copy.states.moderate, safe: copy.states.good })[safety.level] || safety.label;
  const beaufortLabel = copy.beaufort[beaufort.desc] || beaufort.desc;
  const cloudNameLabel = copy.cloudNames[cloudName.name] || cloudName.name;
  const forecastItems = [
    { label: copy.maxWind, value: `${todayForecast.windMax} kt`, detail: `${copy.gusts} ${todayForecast.gustMax} kt`, to: `${guidePrefix}/max-wind-guide` },
    { label: copy.maxWaves, value: `${todayForecast.waveMax} m`, detail: `${copy.swell} ${todayForecast.swellMax} m`, to: `${guidePrefix}/max-waves-guide` },
    { label: copy.airHigh, value: `${todayForecast.tempMax}°C`, detail: copy.today, to: `${guidePrefix}/air-high-guide` },
  ];
  const metricTopics = [
    {
      eyebrow: copy.windSeaEyebrow,
      title: copy.windSeaTitle,
      description: copy.windSeaDescription,
      items: [
        { icon: Wind, label: copy.labels.wind, value: `${windKnots} kt`, helper: `${copy.gusts} ${gustKnots} kt`, to: `${guidePrefix}/wind-guide` },
        { icon: Wind, label: copy.labels.gusts, value: `${gustKnots} kt`, helper: `${copy.helpers.gustSpread} ${Math.max(0, gustKnots - windKnots)} kt`, to: `${guidePrefix}/gusts-guide` },
        { icon: Waves, label: copy.labels.seaState, value: `${waveM} m`, helper: copy.helpers.liveWave, to: `${guidePrefix}/sea-state-guide` },
        { icon: Gauge, label: copy.labels.beaufort, value: `Bft ${beaufort.force}`, helper: beaufortLabel, to: `${guidePrefix}/beaufort-guide` },
      ],
    },
    {
      eyebrow: copy.skyEyebrow,
      title: copy.skyTitle,
      description: copy.skyDescription,
      items: [
        { icon: Eye, label: copy.labels.visibility, value: `${todayForecast.visibilityKm} km`, helper: todayForecast.visibilityKm < 5 ? copy.helpers.reducedVisibility : copy.helpers.goodVisibility, to: `${guidePrefix}/visibility-guide` },
        { icon: Droplets, label: copy.labels.humidity, value: `${todayForecast.humidity}%`, helper: todayForecast.humidity >= 95 ? copy.helpers.highFog : todayForecast.humidity >= 80 ? copy.helpers.fogRising : todayForecast.humidity >= 60 ? copy.helpers.moistAir : copy.helpers.clearAir, to: `${guidePrefix}/relative-humidity-guide` },
        { icon: Cloud, label: copy.labels.cloudCoverage, value: `${todayForecast.cloudCover}%`, helper: todayForecast.cloudCover < 25 ? copy.helpers.mostlyClear : todayForecast.cloudCover < 60 ? copy.helpers.partlyCloudy : copy.helpers.mostlyCloudy, to: `${guidePrefix}/cloud-coverage-guide` },
        { icon: CloudRain, label: copy.labels.rain, value: `${todayForecast.rain} mm`, helper: todayForecast.rain > 0 ? copy.helpers.rainNow : copy.helpers.noRain, to: `${guidePrefix}/rain-guide` },
        { icon: AlertTriangle, label: copy.labels.stormTrend, value: stormTrend.value, helper: stormTrend.helper, to: `${guidePrefix}/storm-approach-trend-guide` },
        { icon: cloudName.icon, label: copy.labels.cloudName, value: cloudNameLabel, helper: copy.helpers.cloudBased, to: `${guidePrefix}/cloud-name-guide` },
        { icon: Cloud, label: copy.labels.cloudType, value: cloudType, helper: copy.helpers.cloudLearn, to: `${guidePrefix}/cloud-type-guide` },
      ],
    },
    {
      eyebrow: copy.planningEyebrow,
      title: copy.planningTitle,
      description: copy.planningDescription,
      items: [
        { icon: Anchor, label: copy.labels.decision, value: safetyLabel, helper: copy.helpers.safetyStatus, to: `${guidePrefix}/sailing-decision-guide` },
        { icon: Gauge, label: copy.labels.airPressure, value: `${(todayForecast.pressureHpa / 1000).toFixed(3)} bar`, helper: `${todayForecast.pressureHpa} ${copy.helpers.seaLevel}`, to: `${guidePrefix}/air-pressure-guide` },
        { icon: Sun, label: copy.labels.uvIndex, value: todayForecast.uvIndex, helper: todayForecast.uvIndex >= 8 ? copy.helpers.veryHighSun : todayForecast.uvIndex >= 6 ? copy.helpers.highSun : todayForecast.uvIndex >= 3 ? copy.helpers.moderateSun : copy.helpers.lowSun, to: `${guidePrefix}/uv-index-guide` },
        { icon: Sunrise, label: copy.labels.sunrise, value: todayForecast.sunrise, helper: copy.helpers.basedLocation, to: `${guidePrefix}/sun-times-guide` },
        { icon: Sunset, label: copy.labels.sunset, value: todayForecast.sunset, helper: copy.helpers.basedLocation, to: `${guidePrefix}/sun-times-guide` },
      ],
    },
  ];

  return (
    <section className="weather-dark-card relative grid gap-3 lg:grid-cols-[0.95fr_1.35fr]" aria-label={copy.commandCenter || "Marine weather command summary"}>
      <Card className="overflow-hidden rounded-[2rem] border-cyan-200/35 !bg-slate-950 !text-white shadow-2xl shadow-cyan-950/35 backdrop-blur-2xl ring-1 ring-cyan-100/15">
        <CardContent className="relative p-4 sm:p-5">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_15%,rgba(34,211,238,0.34),transparent_24rem),radial-gradient(circle_at_82%_12%,rgba(99,102,241,0.22),transparent_22rem),linear-gradient(135deg,rgba(15,23,42,0.16),transparent)]" />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-200/70 to-transparent" />
          <div className="relative">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.28em] text-cyan-200">{copy.commandIndex}</p>
                <h2 className="mt-1 text-xl font-black tracking-tight text-white sm:text-2xl">{copy.passageReadiness}</h2>
                <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-sm font-bold text-cyan-50 ring-1 ring-white/15">
                  <MapPin className="h-4 w-4 text-cyan-200" /> {displayCity}
                </p>
              </div>
              <div className="grid h-12 w-12 place-items-center rounded-2xl border border-cyan-200/20 bg-cyan-300/10 shadow-[0_0_30px_rgba(34,211,238,0.18)]"><ShieldCheck className="h-6 w-6 text-cyan-200" /></div>
            </div>
            <div className="mt-5 grid place-items-center">
              <div className="relative grid h-36 w-36 place-items-center rounded-full border-[12px] border-white/10 border-l-cyan-300 border-r-indigo-300 border-t-cyan-300 shadow-[0_0_48px_rgba(34,211,238,0.30)] before:absolute before:inset-[-18px] before:rounded-full before:border before:border-cyan-200/10 before:content-['']">
                <div className="text-center">
                  <p className="font-mono text-4xl font-black tabular-nums text-white">{score}</p>
                  <p className="text-xs font-bold uppercase tracking-widest text-cyan-100">{copy.outOf100}</p>
                </div>
              </div>
            </div>
            <div className="mt-5 rounded-2xl border border-cyan-200/15 bg-white/[0.06] p-3 shadow-inner shadow-cyan-950/20 backdrop-blur-xl">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-black text-white">{copy.todayForecast}</p>
                  <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-cyan-50/75"><MapPin className="h-3.5 w-3.5" /> {displayLocation}</p>
                </div>
                <span className="rounded-full border border-cyan-200/20 bg-cyan-300/15 px-2 py-1 text-[10px] font-black uppercase tracking-wider text-cyan-100">{copy.live}</span>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {forecastItems.map((item) => (
                  <Link key={item.label} to={item.to} className="rounded-xl border border-cyan-200/25 !bg-slate-900 p-2 !text-white shadow-sm shadow-cyan-950/20 transition-colors hover:border-cyan-200/60 hover:!bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300">
                    <p className="text-[10px] font-black uppercase tracking-wider text-cyan-100/80">{item.label}</p>
                    <p className="mt-1 font-mono text-sm font-black text-white">{item.value}</p>
                    <p className="text-[10px] text-cyan-50/65">{item.detail}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <WeatherMetricTopicGroup topics={metricTopics} />
    </section>
  );
}